import Link from "next/link";
import { Logo } from "@/components/layout/logo";
import { siteConfig, featuredServices, industries } from "@/lib/data/site";

export function Footer() {
  return (
    <footer className="relative mt-16 overflow-hidden border-t border-border bg-background-elevated sm:mt-20 md:mt-24">
      <div className="aurora opacity-40" />
      <div className="container-wide relative py-12 sm:py-16 md:py-24">
        <div className="mb-10 max-w-4xl sm:mb-14 md:mb-16">
          <p className="eyebrow mb-4 sm:mb-6">Maxridge</p>
          <h2 className="display-title text-[clamp(2rem,8vw,4.5rem)] sm:text-5xl md:text-7xl">
            INTELLIGENCE FOR
            <br />
            <span className="gradient-text">THE ENTERPRISE ERA.</span>
          </h2>
        </div>

        <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-5">
          <div className="sm:col-span-2 lg:col-span-2">
            <Logo />
            <p className="mt-4 max-w-sm text-sm text-muted leading-relaxed">{siteConfig.tagline}</p>
            <p className="mt-4 max-w-sm text-sm leading-relaxed text-muted">
              <span className="block text-xs font-semibold uppercase tracking-[0.14em] text-muted/80">
                Office
              </span>
              <a
                href={siteConfig.maps.directionsUrl}
                target="_blank"
                rel="noreferrer"
                className="mt-1.5 block text-foreground/90 transition hover:text-accent"
              >
                {siteConfig.address.building}
                <br />
                {siteConfig.address.line1}
                <br />
                {siteConfig.address.line2}
              </a>
            </p>
            <form className="mt-6 flex max-w-md flex-col gap-2 sm:flex-row" action="/contact">
              <label className="sr-only" htmlFor="newsletter-email">
                Email for newsletter
              </label>
              <input
                id="newsletter-email"
                name="email"
                type="email"
                autoComplete="email"
                inputMode="email"
                placeholder="Work email"
                data-gramm="false"
                data-gramm_editor="false"
                data-enable-grammarly="false"
                suppressHydrationWarning
                className="h-11 w-full flex-1 rounded-full border border-border bg-card px-4 text-sm outline-none focus:border-accent"
              />
              <button
                type="submit"
                className="h-11 shrink-0 rounded-full bg-foreground px-5 text-sm font-medium text-background"
              >
                Subscribe
              </button>
            </form>
          </div>

          <div>
            <p className="mb-4 text-xs font-semibold uppercase tracking-[0.16em] text-muted">Company</p>
            <ul className="space-y-2 text-sm">
              {[
                ["About", "/about"],
                ["Careers", "/careers"],
                ["Insights", "/insights"],
                ["Case Studies", "/case-studies"],
                ["Contact", "/contact"],
              ].map(([label, href]) => (
                <li key={href}>
                  <Link href={href} className="text-muted hover:text-foreground">
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="mb-4 text-xs font-semibold uppercase tracking-[0.16em] text-muted">Services</p>
            <ul className="space-y-2 text-sm">
              {featuredServices.map((s) => (
                <li key={s.id}>
                  <Link href={s.href} className="text-muted hover:text-foreground">
                    {s.title.split("&")[0].trim()}
                  </Link>
                </li>
              ))}
              <li>
                <Link href="/services" className="text-muted hover:text-foreground">
                  All services
                </Link>
              </li>
              <li>
                <Link href="/locations/noida" className="text-muted hover:text-foreground">
                  Noida
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <p className="mb-4 text-xs font-semibold uppercase tracking-[0.16em] text-muted">Explore</p>
            <ul className="space-y-2 text-sm">
              <li>
                <Link href="/solutions" className="text-muted hover:text-foreground">
                  Solutions
                </Link>
              </li>
              <li>
                <Link href="/technology" className="text-muted hover:text-foreground">
                  Technology
                </Link>
              </li>
              {industries.slice(0, 4).map((i) => (
                <li key={i.slug}>
                  <Link href={`/industries/${i.slug}`} className="text-muted hover:text-foreground">
                    {i.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-16 flex flex-col gap-4 border-t border-border pt-8 text-sm text-muted md:flex-row md:items-center md:justify-between">
          <p>© {new Date().getFullYear()} Maxridge. All rights reserved.</p>
          <div className="flex flex-wrap gap-4">
            <Link href="/privacy" className="hover:text-foreground">
              Privacy
            </Link>
            <Link href="/terms" className="hover:text-foreground">
              Terms
            </Link>
            <a href={siteConfig.social.linkedin} className="hover:text-foreground" rel="noreferrer" target="_blank">
              LinkedIn
            </a>
            <a href={siteConfig.social.twitter} className="hover:text-foreground" rel="noreferrer" target="_blank">
              X
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}